import React, { useEffect, useRef, useState } from "react";
import { Globe, Play, Pause } from "lucide-react";
import { LANGUAGES, languageMeta } from "@/lib/languages";
import { useLang } from "@/context/LanguageContext";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

// Scrollable language list with a button that slowly auto-scrolls through the
// languages (loops). Shared by both trigger variants.
function LangList({ itemClass = "gap-2.5", flagClass = "text-lg" }) {
  const { t } = useLang();
  const scrollRef = useRef(null);
  const rafRef = useRef(null);
  const [scrolling, setScrolling] = useState(false);

  useEffect(() => {
    if (!scrolling) return;
    const el = scrollRef.current;
    if (!el) return;
    let last = performance.now();
    const step = (now) => {
      const dt = now - last; last = now;
      el.scrollTop += 0.03 * dt; // ~30px/s — slow, gentle
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 1) el.scrollTop = 0;
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [scrolling]);

  return (
    <div>
      <button
        type="button"
        onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setScrolling((s) => !s); }}
        className="w-full flex items-center justify-center gap-1.5 py-2 mb-1 text-xs font-semibold text-findo-navy border-b border-border hover:bg-slate-50 transition-colors sticky top-0 bg-white z-10"
        data-testid="lang-autoscroll-toggle">
        {scrolling ? <Pause size={13} /> : <Play size={13} />}
        {scrolling ? t("hc.stopScrolling") : t("hc.scrollThroughLanguages")}
      </button>
      <div ref={scrollRef} className="max-h-40 overflow-y-auto" data-testid="lang-scroll-area">
        {LANGUAGES.map((l) => (
          <SelectItem key={l.code} value={l.code} data-testid={`lang-${l.code}`}>
            <span className={`flex items-center ${itemClass}`}><span className={flagClass}>{l.flag}</span> {l.name}</span>
          </SelectItem>
        ))}
      </div>
    </div>
  );
}

export default function LanguageSelect({ variant = "header", testid = "language-select" }) {
  const { lang, setLang } = useLang();
  const meta = languageMeta(lang);

  if (variant === "compact") {
    return (
      <Select value={lang} onValueChange={setLang}>
        <SelectTrigger data-testid={testid} className="h-9 w-[68px] rounded-full px-3 gap-1 border-border">
          <span className="text-base leading-none">{meta.flag}</span>
        </SelectTrigger>
        <SelectContent className="max-h-none">
          <LangList itemClass="gap-2" flagClass="text-base" />
        </SelectContent>
      </Select>
    );
  }

  return (
    <Select value={lang} onValueChange={setLang}>
      <SelectTrigger data-testid={testid} className="h-11 w-56 rounded-full pl-4 pr-3 gap-2 bg-white border-border font-semibold">
        <Globe size={17} className="text-findo-navy shrink-0" />
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="max-h-none">
        <LangList />
      </SelectContent>
    </Select>
  );
}
